import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import moment from "moment";
import { dateUtils } from "../../utils";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useDeleteFramesBatch } from "../../hooks/useDevices";

// frames: [{ fcnt, time_created }] with time_created as returned by the API (ISO string)
export default function DeleteFramesDialog({
  deviceId,
  frames = [],
  open,
  handleClose,
}) {
  const deleteFramesMutation = useDeleteFramesBatch();
  const { showSuccess, showError } = useSnackbar();

  const handleDelete = () => {
    const payload = frames.map((frame) => ({
      fcnt: frame.fcnt,
      time_created: moment(frame.time_created).unix(),
    }));

    deleteFramesMutation.mutate(
      { deviceId, frames: payload },
      {
        onSuccess: () => {
          showSuccess(`Deleted ${frames.length} frame(s)`);
          handleClose();
        },
        onError: (error) => {
          showError(
            error.response?.data?.detail || "Error deleting frames",
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Frames</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete {frames.length} selected frame
          {frames.length === 1 ? "" : "s"}? This cannot be undone.
        </Typography>
        {frames.length > 0 && (
          <Box sx={{ maxHeight: 240, overflowY: "auto", mt: 1 }}>
            <List dense disablePadding>
              {frames.map((frame, i) => (
                <ListItem
                  key={`${frame.fcnt}-${frame.time_created}-${i}`}
                  disableGutters
                >
                  <ListItemText
                    primary={`fCnt ${frame.fcnt}`}
                    secondary={dateUtils.formatDate(frame.time_created)}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDelete}
          color="error"
          disabled={!frames.length || deleteFramesMutation.isPending}
        >
          {deleteFramesMutation.isPending ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
